// src/components/BugReporterOverlay.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { addReport } from '../lib/telemetry/db';
import { uploadScreenshot } from '../lib/telemetry/storage';
import { getAuth } from 'firebase/auth';

type Props = { buttonLabel?: string };

export default function BugReporterOverlay({ buttonLabel = 'Report a bug' }: Props) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [shotBlob, setShotBlob] = useState<Blob | null>(null);
  const [busy, setBusy] = useState(false);
  const [cooling, setCooling] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Get current page path without React Router dependency
  const getCurrentPath = () => {
    try {
      return window.location.pathname;
    } catch (e) {
      return '/';
    }
  };

  // Close on Esc
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const takeScreenshot = useCallback(async () => {
    try {
      console.log('🎯 Starting screenshot capture...');
      
      // Dynamically import to keep bundle lean
      const html2canvas = (await import('html2canvas')).default;
      console.log('✅ html2canvas imported successfully');

      // Temporarily hide overlay so it doesn't appear in the shot
      const node = overlayRef.current;
      const prevVisibility = node?.style.visibility;
      if (node) node.style.visibility = 'hidden';
      console.log('👻 Overlay hidden for screenshot');

      // Give layout a tick
      await new Promise(r => requestAnimationFrame(() => r(null as any)));

      try {
        console.log('📸 Capturing screenshot with html2canvas...');
        const canvas = await html2canvas(document.body, {
          useCORS: true,
          logging: false,
          ignoreElements: (el) => (el as HTMLElement)?.dataset?.ignoreCapture === 'true',
          windowWidth: document.documentElement.scrollWidth,
          windowHeight: document.documentElement.scrollHeight,
        });
        console.log('✅ Canvas created:', { width: canvas.width, height: canvas.height });

        // Downscale to max width 1200 for size control
        const maxW = 1200;
        const scale = Math.min(1, maxW / canvas.width);
        console.log('📏 Scale factor:', scale);
        
        let outCanvas = canvas;
        if (scale < 1) {
          const c = document.createElement('canvas');
          c.width = Math.floor(canvas.width * scale);
          c.height = Math.floor(canvas.height * scale);
          const ctx = c.getContext('2d')!;
          ctx.drawImage(canvas, 0, 0, c.width, c.height);
          outCanvas = c;
          console.log('🔄 Canvas resized to:', { width: c.width, height: c.height });
        }
        
        const blob: Blob = await new Promise((res) => outCanvas.toBlob(b => res(b!), 'image/png', 0.92));
        console.log('💾 Blob created:', { size: blob.size, type: blob.type });
        
        setShotBlob(blob);
        const previewUrl = URL.createObjectURL(blob);
        setPreviewUrl(previewUrl);
        console.log('🖼️ Preview URL created:', previewUrl);
      } catch (e) {
        console.error('❌ Screenshot failed:', e);
        setShotBlob(null);
        setPreviewUrl(null);
      } finally {
        if (node) node.style.visibility = prevVisibility || '';
        console.log('👁️ Overlay visibility restored');
      }
    } catch (e) {
      console.error('❌ html2canvas import failed:', e);
      setShotBlob(null);
      setPreviewUrl(null);
    }
  }, []);

  const openModal = async () => {
    console.log('🚀 Opening bug report modal...');
    await takeScreenshot();
    setOpen(true);
  };

  const submit = async () => {
    if (busy || cooling) return;
    if (!message.trim() && !shotBlob) return; // require something
    setBusy(true);
    try {
      let screenshotUrl: string | null = null;
      if (shotBlob) {
        screenshotUrl = await uploadScreenshot(shotBlob);
      }
      const auth = getAuth();
      const u = auth.currentUser;

      await addReport({
        type: 'feedback',
        message: message.trim() || '(screenshot only)',
        pagePath: getCurrentPath(),
        userId: u?.uid ?? null,
        userEmail: u?.email ?? null,
        userAgent: navigator.userAgent,
        screenshotUrl,
      });

      setOpen(false);
      setMessage('');
      setShotBlob(null);
      setPreviewUrl(null);
      // Cooldown 5s
      setCooling(true);
      setTimeout(() => setCooling(false), 5000);
    } catch (error) {
      console.error('Failed to submit bug report:', error);
      alert('Failed to submit bug report. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // Only show for authenticated users
  const auth = getAuth();
  const isAuthenticated = auth.currentUser !== null;

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <div
        ref={overlayRef}
        data-ignore-capture="true"
        style={{
          position: 'fixed',
          bottom: '16px',
          right: '16px',
          zIndex: 50,
          pointerEvents: 'auto'
        }}
      >
        <button
          type="button"
          aria-label={buttonLabel}
          style={{
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            background: 'white',
            color: 'black',
            border: '1px solid #d1d5db',
            cursor: cooling ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            opacity: cooling ? 0.6 : 1,
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            if (!cooling) {
              e.currentTarget.style.background = '#f9fafb';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
          }}
          onClick={openModal}
          disabled={cooling}
          title={cooling ? 'Please wait a moment…' : buttonLabel}
        >
          🐛 {buttonLabel}
        </button>
      </div>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.4)'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setOpen(false);
            }
          }}
        >
          <div style={{
            width: '100%',
            maxWidth: '600px',
            borderRadius: '12px',
            background: 'white',
            padding: '24px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            margin: '20px'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '16px',
              color: '#1f2937'
            }}>
              🐛 Submit a bug report
            </h2>

            {previewUrl ? (
              <div style={{
                marginTop: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                overflow: 'hidden',
                maxHeight: '256px'
              }}>
                <img 
                  src={previewUrl} 
                  alt="Screenshot preview" 
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover'
                  }}
                />
              </div>
            ) : (
              <div style={{
                marginTop: '12px',
                fontSize: '14px',
                color: '#6b7280',
                textAlign: 'center'
              }}>
                <p>No screenshot captured. You can still send text feedback.</p>
                <button
                  type="button"
                  onClick={takeScreenshot}
                  style={{
                    marginTop: '8px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  📸 Try Screenshot Again
                </button>
              </div>
            )}

            <label style={{
              display: 'block',
              marginTop: '16px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151'
            }}>
              What's wrong? (optional)
            </label>
            <textarea
              style={{
                marginTop: '4px',
                width: '100%',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '8px',
                minHeight: '90px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Brief description or steps to reproduce the issue..."
            />

            <div style={{
              marginTop: '16px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}>
              <button
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  opacity: busy ? 0.6 : 1
                }}
                onClick={() => setOpen(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  background: busy ? '#6b7280' : '#1f2937',
                  color: 'white',
                  border: 'none',
                  fontSize: '14px',
                  cursor: (busy || (message.trim().length === 0 && !shotBlob)) ? 'not-allowed' : 'pointer',
                  opacity: (busy || (message.trim().length === 0 && !shotBlob)) ? 0.6 : 1
                }}
                onClick={submit}
                disabled={busy || (message.trim().length === 0 && !shotBlob)}
              >
                {busy ? 'Sending…' : 'Send Report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
